import {EffectCards} from "../constants";

/**
 * 像素化效果
 * @param {HTMLCanvasElement} canvas 目标画布
 * @param {number} intensity 效果强度 (0-1)
 */
export const applyPixelateEffect = (canvas: HTMLCanvasElement, intensity: number) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return ;

    const pixelSize = Math.max(2, 20 * intensity);
    const w = canvas.width;
    const h = canvas.height;

    // 获取图像数据
    const imageData = ctx.getImageData(0, 0, w, h);
    const data = imageData.data;

    // 应用像素化
    for (let y = 0; y < h; y += pixelSize) {
        for (let x = 0; x < w; x += pixelSize) {
            // 获取当前块的像素位置
            const pixelPos = (y * w + x) * 4;

            // 获取当前块的平均颜色
            let r = 0, g = 0, b = 0, count = 0;
            for (let dy = 0; dy < pixelSize && y + dy < h; dy++) {
                for (let dx = 0; dx < pixelSize && x + dx < w; dx++) {
                    const blockPos = ((y + dy) * w + (x + dx)) * 4;
                    r += data[blockPos];
                    g += data[blockPos + 1];
                    b += data[blockPos + 2];
                    count++;
                }
            }

            r = Math.round(r / count);
            g = Math.round(g / count);
            b = Math.round(b / count);

            // 应用平均颜色到整个块
            for (let dy = 0; dy < pixelSize && y + dy < h; dy++) {
                for (let dx = 0; dx < pixelSize && x + dx < w; dx++) {
                    const blockPos = ((y + dy) * w + (x + dx)) * 4;
                    data[blockPos] = r;
                    data[blockPos + 1] = g;
                    data[blockPos + 2] = b;
                }
            }
        }
    }

    // 放回处理后的图像数据
    ctx.putImageData(imageData, 0, 0);
};

/**
 * 水彩效果
 * @param {HTMLCanvasElement} canvas 目标画布
 * @param {number} intensity 效果强度 (0-1)
 */
export const applyWatercolorEffect = (canvas: HTMLCanvasElement, intensity: number) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return ;

    const radius = Math.max(3, 15 * intensity);
    const w = canvas.width;
    const h = canvas.height;

    // 创建临时Canvas
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = w;
    tempCanvas.height = h;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return ;

    // 绘制模糊版本
    tempCtx.drawImage(canvas, 0, 0);
    tempCtx.filter = `blur(${radius}px)`;
    tempCtx.drawImage(tempCanvas, 0, 0);
    tempCtx.filter = 'none';

    // 混合原始图像和模糊图像
    ctx.globalAlpha = 0.7;
    ctx.drawImage(tempCanvas, 0, 0);
    ctx.globalAlpha = 1.0;

    // 添加一些噪点模拟水彩纹理
    const imageData = ctx.getImageData(0, 0, w, h);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
        if (Math.random() < 0.3 * intensity) {
            const noise = Math.floor(Math.random() * 30) - 15;
            data[i] = Math.min(255, Math.max(0, data[i] + noise));
            data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + noise));
            data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + noise));
        }
    }

    ctx.putImageData(imageData, 0, 0);
};

/**
 * 故障艺术效果
 * @param {HTMLCanvasElement} canvas 目标画布
 * @param {number} intensity 效果强度 (0-1)
 */
export const applyGlitchEffect = (canvas: HTMLCanvasElement, intensity: number) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return ;

    const w = canvas.width;
    const h = canvas.height;

    // 获取图像数据
    const imageData = ctx.getImageData(0, 0, w, h);
    const data = imageData.data;

    // 应用故障效果
    const shiftAmount = Math.floor(10 * intensity);
    const glitchLines = Math.floor(h * 0.2 * intensity);

    for (let i = 0; i < glitchLines; i++) {
        const y = Math.floor(Math.random() * h);
        const shift = Math.floor(Math.random() * shiftAmount * 2) - shiftAmount;

        for (let x = 0; x < w; x++) {
            const newX = (x + shift + w) % w;
            const pos = (y * w + x) * 4;
            const newPos = (y * w + newX) * 4;

            // 只复制一个颜色通道以创建RGB分离效果
            const channel = Math.floor(Math.random() * 3);
            data[pos + channel] = data[newPos + channel];
        }
    }

    // 添加一些随机噪声
    for (let i = 0; i < data.length; i += 4) {
        if (Math.random() < 0.02 * intensity) {
            data[i] = Math.floor(Math.random() * 256);     // R
            data[i + 1] = Math.floor(Math.random() * 256); // G
            data[i + 2] = Math.floor(Math.random() * 256); // B
        }
    }

    // 放回处理后的图像数据
    ctx.putImageData(imageData, 0, 0);
};

/**
 * 霓虹发光效果
 * @param {HTMLCanvasElement} canvas 目标画布
 * @param {number} intensity 效果强度 (0-1)
 */
export const applyNeonEffect = (canvas: HTMLCanvasElement, intensity: number) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return ;

    const w = canvas.width;
    const h = canvas.height;

    // 创建临时Canvas用于边缘检测
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = w;
    tempCanvas.height = h;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return ;

    // 绘制灰度版本
    tempCtx.drawImage(canvas, 0, 0);
    const imageData = tempCtx.getImageData(0, 0, w, h);
    const data = imageData.data;

    // 转换为灰度
    for (let i = 0; i < data.length; i += 4) {
        const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
        data[i] = data[i + 1] = data[i + 2] = avg;
    }
    tempCtx.putImageData(imageData, 0, 0);

    // 应用边缘检测
    tempCtx.filter = 'brightness(2) contrast(2)';
    tempCtx.drawImage(tempCanvas, 0, 0);
    tempCtx.filter = 'none';

    // 获取边缘数据
    const edgeData = tempCtx.getImageData(0, 0, w, h).data;

    // 在原始图像上应用霓虹效果
    const originalData = ctx.getImageData(0, 0, w, h);
    const originalPixels = originalData.data;

    for (let i = 0; i < originalPixels.length; i += 4) {
        if (edgeData[i] > 150) { // 如果是边缘
            // 增强颜色
            originalPixels[i] = Math.min(255, originalPixels[i] * (1 + intensity));     // R
            originalPixels[i + 1] = Math.min(255, originalPixels[i + 1] * (1 + intensity)); // G
            originalPixels[i + 2] = Math.min(255, originalPixels[i + 2] * (1 + intensity)); // B

            // 添加发光效果
            const glowSize = Math.floor(3 * intensity);
            const x = (i / 4) % w;
            const y = Math.floor((i / 4) / w);

            for (let dy = -glowSize; dy <= glowSize; dy++) {
                for (let dx = -glowSize; dx <= glowSize; dx++) {
                    const newX = x + dx;
                    const newY = y + dy;

                    if (newX >= 0 && newX < w && newY >= 0 && newY < h) {
                        const dist = Math.sqrt(dx * dx + dy * dy);
                        if (dist <= glowSize) {
                            const pos = (newY * w + newX) * 4;
                            const glowIntensity = (glowSize - dist) / glowSize * intensity;
                            originalPixels[pos] = Math.min(255, originalPixels[pos] + 100 * glowIntensity);
                            originalPixels[pos + 1] = Math.min(255, originalPixels[pos + 1] + 100 * glowIntensity);
                            originalPixels[pos + 2] = Math.min(255, originalPixels[pos + 2] + 100 * glowIntensity);
                        }
                    }
                }
            }
        }
    }

    ctx.putImageData(originalData, 0, 0);
};

/**
 * 马赛克效果
 * @param {HTMLCanvasElement} canvas 目标画布
 * @param {number} intensity 效果强度 (0-1)
 */
export const applyMosaicEffect = (canvas: HTMLCanvasElement, intensity: number) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return ;

    const blockSize = Math.max(5, 50 * intensity);
    const w = canvas.width;
    const h = canvas.height;

    // 获取图像数据
    const imageData = ctx.getImageData(0, 0, w, h);
    const data = imageData.data;

    // 应用马赛克
    for (let y = 0; y < h; y += blockSize) {
        for (let x = 0; x < w; x += blockSize) {
            // 获取当前块的随机颜色
            const randomX = x + Math.floor(Math.random() * blockSize);
            const randomY = y + Math.floor(Math.random() * blockSize);

            if (randomX >= w || randomY >= h) continue;

            const randomPos = (randomY * w + randomX) * 4;
            const r = data[randomPos];
            const g = data[randomPos + 1];
            const b = data[randomPos + 2];

            // 应用随机颜色到整个块
            for (let dy = 0; dy < blockSize && y + dy < h; dy++) {
                for (let dx = 0; dx < blockSize && x + dx < w; dx++) {
                    const pos = ((y + dy) * w + (x + dx)) * 4;
                    data[pos] = r;
                    data[pos + 1] = g;
                    data[pos + 2] = b;
                }
            }
        }
    }

    // 放回处理后的图像数据
    ctx.putImageData(imageData, 0, 0);
};

/**
 * 素描效果
 * @param {HTMLCanvasElement} canvas 目标画布
 * @param {number} intensity 效果强度 (0-1)
 */
export const applySketchEffect = (canvas: HTMLCanvasElement, intensity: number) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return ;

    const w = canvas.width;
    const h = canvas.height;

    // 创建临时Canvas用于边缘检测
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = w;
    tempCanvas.height = h;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return ;

    // 绘制灰度版本
    tempCtx.drawImage(canvas, 0, 0);
    const imageData = tempCtx.getImageData(0, 0, w, h);
    const data = imageData.data;

    // 转换为灰度
    for (let i = 0; i < data.length; i += 4) {
        const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
        data[i] = data[i + 1] = data[i + 2] = avg;
    }
    tempCtx.putImageData(imageData, 0, 0);

    // 应用边缘检测
    tempCtx.filter = 'invert(1) contrast(2)';
    tempCtx.drawImage(tempCanvas, 0, 0);
    tempCtx.filter = 'none';

    // 获取边缘数据
    const edgeData = tempCtx.getImageData(0, 0, w, h).data;

    // 创建素描效果
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, w, h);

    const sketchData = ctx.getImageData(0, 0, w, h);
    const sketchPixels = sketchData.data;

    const lineDensity = Math.floor(5 + 10 * (1 - intensity));

    // 添加素描线条
    for (let y = 0; y < h; y += lineDensity) {
        for (let x = 0; x < w; x++) {
            const pos = (y * w + x) * 4;

            if (edgeData[pos] < 200) { // 如果是边缘区域
                const darkness = 255 - edgeData[pos];
                sketchPixels[pos] = sketchPixels[pos + 1] = sketchPixels[pos + 2] = Math.max(0, 255 - darkness * 2);
            }
        }
    }

    // 添加一些噪点模拟铅笔纹理
    for (let i = 0; i < sketchPixels.length; i += 4) {
        if (Math.random() < 0.1 * intensity) {
            const noise = Math.floor(Math.random() * 50);
            sketchPixels[i] = sketchPixels[i + 1] = sketchPixels[i + 2] = Math.max(0, sketchPixels[i] - noise);
        }
    }

    ctx.putImageData(sketchData, 0, 0);
};

/**
 * 根据效果名称应用相应的效果
 * @param {string} effect 效果名称
 * @param {HTMLCanvasElement} canvas 目标画布
 * @param {number} intensity 效果强度 (0-1)
 */
export const applyEffect = (effect: string, canvas: HTMLCanvasElement, intensity = 0.5) => {
    switch(effect) {
        case 'pixelate':
            applyPixelateEffect(canvas, intensity);
            break;
        case 'watercolor':
            applyWatercolorEffect(canvas, intensity);
            break;
        case 'glitch':
            applyGlitchEffect(canvas, intensity);
            break;
        case 'neon':
            applyNeonEffect(canvas, intensity);
            break;
        case 'mosaic':
            applyMosaicEffect(canvas, intensity);
            break;
        case 'sketch':
            applySketchEffect(canvas, intensity);
            break;
        default:
            console.warn(`未知效果: ${effect}`);
    }
};

/**
 * 获取效果名称
 * @param {string} effect 效果ID
 * @returns {string} 效果名称
 */
export const getEffectName = (effect: string) => {
    const names = Object.fromEntries(EffectCards.map(item => [item.id, item.title]));
    return effect in names ? names[effect as keyof typeof names] : effect;
};
